'use client'
import { type FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { Box, Button, Divider, Flex, HStack, SimpleGrid, VStack } from '@chakra-ui/react'
import { MdProductionQuantityLimits } from 'react-icons/md'
import { AiFillFilter } from 'react-icons/ai'
import { ActivityLoader, EmptyMessage, GrandSection } from 'ui'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useOnScreen } from 'ui/hooks'
import { overrideText } from 'ui/lib/overrides'
import type { ProductLightDTO } from 'ecosystem'
import ProductListItemComponent, { type ProductListItemProps } from '../../ProductListItem'
import { ProductListSearchMoreFilters } from '../ProductListSearchMoreFilters'
import type { ProductListSearchProps } from '../types'

export type ProductListSearchVerticalProps = Omit<ProductListSearchProps, 'variant'>

export interface ProductListSearchOverrides {
  sort?: string
  productListSearchTitle?: string
  viewTotalProducts?: string
  noProductsFound?: string
}

export const ProductListSearchVertical: FC<ProductListSearchVerticalProps> = ({
  isLoading,
  title,
  items,
  totalItems,
  deliveryOptions,
  infiniteScrollConfig,
  searchProductsState,
  onMore,
  onAddToCart,
  cartItems,
  onProductRequest,
  productsUrlPath,
  customComponents,
  filterProps,
  columnsNumber: _columnsNumber,
  columnsNumberTablet: _columnsNumberTablet,
  columnsNumberMobile: _columnsNumberMobile,
  imageAspectRatio: _imageAspectRatio,
  imageAspectRatioTablet,
  imageAspectRatioMobile,
  ...props
}) => {
  const overrides = useOverridesContext<keyof ProductListSearchOverrides>()

  const columnsNumber = (_columnsNumber ?? 0) > 0 ? _columnsNumber! : 4
  const columnsNumberTablet =
    (_columnsNumberTablet ?? 0) > 0 ? _columnsNumberTablet! : columnsNumber
  const columnsNumberMobile = (_columnsNumberMobile ?? 0) > 0 ? _columnsNumberMobile! : 2

  const imageAspectRatio = useMemo<ProductListItemProps['imageAspectRatio']>(() => {
    if (!_imageAspectRatio && !imageAspectRatioTablet && !imageAspectRatioMobile) {
      return undefined
    }

    if (imageAspectRatioMobile || imageAspectRatioTablet) {
      return {
        ...(imageAspectRatioMobile || _imageAspectRatio
          ? { base: imageAspectRatioMobile || _imageAspectRatio }
          : {}),
        ...(imageAspectRatioTablet || _imageAspectRatio
          ? { sm: imageAspectRatioTablet || _imageAspectRatio }
          : {}),
        ...(_imageAspectRatio ? { md: _imageAspectRatio } : {})
      }
    }

    return _imageAspectRatio
  }, [_imageAspectRatio, imageAspectRatioMobile, imageAspectRatioTablet])

  // ******** INFINITE SCROLL ********

  const loadMoreIntersectionDiv = useRef<HTMLDivElement | null>(null)
  const isLastElementReached = useOnScreen(loadMoreIntersectionDiv)
  const { shouldLoadMore, loadMore } = infiniteScrollConfig

  useEffect(() => {
    if (isLastElementReached && shouldLoadMore && !isLoading) {
      loadMore()
    }
  }, [isLastElementReached, isLoading, shouldLoadMore, loadMore])

  const onBuy = useCallback(
    (product: ProductLightDTO) => {
      const itemInCart = cartItems.find((item) => item.id === product.id)

      const qty = (itemInCart?.qty ?? 0) + 1

      onAddToCart({
        id: product.id,
        type: 'SINGLE',
        qty,
        meta: {
          singleType: {
            product
          }
        }
      })
    },
    [cartItems, onAddToCart]
  )

  const { ProductListItem = ProductListItemComponent } = customComponents || {}

  return (
    <>
      <GrandSection
        contentContainerProps={{
          spacing: 4
        }}
        {...{ title }}
        {...props}
      >
        <VStack
          align={{
            base: 'flex-start',
            lg: 'center'
          }}
          justify="flex-start"
        >
          <Button
            aria-label="show-filters"
            bg="background.mildDark"
            borderColor="background.mildDark"
            borderRadius="md"
            display={{ base: 'block', md: 'none' }}
            minH="35px"
            minW={100}
            onClick={onMore}
            size="sm"
            w={{ base: 'full', sm: 'auto' }}
          >
            <HStack justify="center" w="full">
              <AiFillFilter />
              <Box>{overrideText('Sortera och filtrera', overrides?.productListSearchTitle)}</Box>
            </HStack>
          </Button>
        </VStack>

        <Divider />

        <HStack align="flex-start" w="full">
          <ProductListSearchMoreFilters
            display={{ base: 'none', md: 'flex' }}
            flex={3}
            pt={1}
            w="auto"
            {...{
              ...filterProps,
              searchProductsState
            }}
          />

          <Box flex={9} minH={500} position="relative">
            {!!items.length && (
              <VStack w="full">
                <HStack justify="space-between" w="full">
                  <Flex
                    alignItems="center"
                    color="text.mild"
                    fontSize="xs"
                    fontWeight="medium"
                    textAlign={{ base: 'right', md: 'left' }}
                  >
                    {`${overrideText(
                      `visar produkter`,
                      overrides?.viewTotalProducts
                    )}: ${totalItems}`}
                  </Flex>
                </HStack>

                <SimpleGrid
                  columns={{
                    base: columnsNumberMobile,
                    sm: columnsNumberTablet,
                    md: columnsNumber
                  }}
                  gap={2}
                  spacing={{
                    base: 1,
                    md: 6
                  }}
                  width="100%"
                >
                  {items.map((item) => (
                    <ProductListItem
                      showsPrice
                      key={item.id}
                      onBuy={() => {
                        onBuy(item)
                      }}
                      imageProps={{
                        sizes: `(max-width: 40em) ${100 / columnsNumberMobile}vw, (max-width: 52em) ${90 / columnsNumberTablet}vw, ${90 / columnsNumber}vw`
                      }}
                      imageAspectRatio={imageAspectRatio}
                      {...{ item, deliveryOptions, onProductRequest, productsUrlPath }}
                    />
                  ))}
                </SimpleGrid>

                {!!isLoading && (
                  <ActivityLoader
                    fontSize="xs"
                    h="full"
                    position="relative"
                    spinnerProps={{ size: 'sm' }}
                    text=""
                    w="full"
                    zIndex={0}
                  />
                )}
              </VStack>
            )}

            {isLoading && !items.length ? (
              <ActivityLoader
                fontSize="xs"
                h="full"
                position="relative"
                spinnerProps={{ size: 'sm' }}
                text=""
                w="full"
                zIndex={0}
              />
            ) : null}

            {!items.length && !isLoading && (
              <Flex align="center" h={500} justify="center">
                <EmptyMessage
                  fontSize="xl"
                  icon={<MdProductionQuantityLimits />}
                  text={overrideText('Inga produkter hittades', overrides?.noProductsFound)}
                />
              </Flex>
            )}
          </Box>
        </HStack>
      </GrandSection>

      <Box h="1px" ref={loadMoreIntersectionDiv} w="full" />
    </>
  )
}
